
import React, { useState, useMemo, useRef, useEffect } from 'react';
import type { Chef, ChefRequest, Recipe, User } from '../types';
import XIcon from './icons/XIcon';
import ChefHatIcon from './icons/ChefHatIcon';
import PlusIcon from './icons/PlusIcon';
import MergeIcon from './icons/MergeIcon';
import ExportIcon from './icons/ExportIcon';
import ImportIcon from './icons/ImportIcon';
import SearchField from './SearchField';
import { getTranslation } from '../utils/translations';
import { getChefDisplayName, inferChefOrigin } from '../utils/chefDisplay';
import { getCountryDisplayName } from '../utils/countryDisplay';
import { downloadChefCsv, importChefsFromCsv, buildNewChefDraft } from '../utils/chefCsv';
import { db } from '../services/firebase';
import ChefEditModal from './ChefEditModal';
import ChefProfileModal from './ChefProfileModal';
import ChefAvatar from './ChefAvatar';
import ChefMergeModal from './ChefMergeModal';
import InfoNoticeModal from './InfoNoticeModal';

interface ChefManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    chefs: Chef[];
    chefRequests: ChefRequest[];
    recipes?: Recipe[];
    onUpdateChef: (id: string, updates: Partial<Chef>) => Promise<void>;
    onCreateChef: (chef: Chef) => Promise<void>;
    onMergeChefs: (keepId: string, mergeId: string) => Promise<string>;
    onApproveRequest: (request: ChefRequest) => Promise<void>;
    onDenyRequest: (requestId: string) => Promise<void>;
}

type Tab = 'chefs' | 'requests';

const ChefManagementModal: React.FC<ChefManagementModalProps> = ({
    isOpen,
    onClose,
    user,
    chefs,
    chefRequests,
    recipes = [],
    onUpdateChef,
    onCreateChef,
    onMergeChefs,
    onApproveRequest,
    onDenyRequest,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(user.language || 'en', key);
    const [tab, setTab] = useState<Tab>('chefs');
    const [search, setSearch] = useState('');
    const [editingChef, setEditingChef] = useState<Chef | null>(null);
    const [viewingChef, setViewingChef] = useState<Chef | null>(null);
    const [isCreatingChef, setIsCreatingChef] = useState(false);
    const [creatingChefDraft, setCreatingChefDraft] = useState<Chef | null>(null);
    const [isMergeOpen, setIsMergeOpen] = useState(false);
    const [mergeSuccessMessage, setMergeSuccessMessage] = useState('');
    const [importing, setImporting] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const pendingRequests = useMemo(
        () => chefRequests.filter((r) => r.status === 'pending'),
        [chefRequests]
    );

    const filteredChefs = useMemo(() => {
        const q = search.toLowerCase().trim();
        if (!q) return chefs;
        return chefs.filter((c) =>
            c.name.toLowerCase().includes(q) ||
            c.englishName.toLowerCase().includes(q) ||
            c.nickname.includes(q) ||
            (c.description?.toLowerCase().includes(q) ?? false) ||
            (c.instagramUsername?.toLowerCase().includes(q) ?? false)
        );
    }, [chefs, search]);

    useEffect(() => {
        if (isOpen) {
            setTab('chefs');
            setSearch('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleImportCsv = async (file: File) => {
        if (!window.confirm(t('importChefCsvConfirm'))) return;
        setImporting(true);
        try {
            const text = await file.text();
            const result = await importChefsFromCsv(db, text);
            alert(
                `${t('importChefCsvDone')}: ${result.imported} ${t('new')}, ${result.updated} ${t('updated')}, ${result.skipped} ${t('skipped')} (${result.total} ${t('total')})`
            );
        } catch (err) {
            console.error(err);
            alert(t('errorSaving'));
        } finally {
            setImporting(false);
        }
    };

    const handleApprove = async (request: ChefRequest) => {
        setActionLoading(request.id);
        try {
            await onApproveRequest(request);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeny = async (requestId: string) => {
        if (!window.confirm(t('denyChefRequest') + '?')) return;
        setActionLoading(requestId);
        try {
            await onDenyRequest(requestId);
        } finally {
            setActionLoading(null);
        }
    };

    const requestsTabClass = tab === 'requests'
        ? 'bg-brand-sage text-white'
        : pendingRequests.length > 0
            ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-500';

    const actionBtn =
        'flex flex-col items-center justify-center gap-1 min-w-0 py-2.5 px-1 rounded-xl text-[9px] font-black uppercase tracking-wide leading-tight transition-all';

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[550] flex items-center justify-center p-4" onClick={onClose}>
                <div
                    className="bg-white dark:bg-slate-900 w-full max-w-2xl max-h-[90vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border dark:border-slate-800"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-6 border-b dark:border-slate-800 shrink-0">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <ChefHatIcon className="w-6 h-6 text-brand-sage" />
                                <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">{t('manageChefs')}</h2>
                            </div>
                            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-full">
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) void handleImportCsv(file);
                                e.target.value = '';
                            }}
                        />
                    </div>

                    <div className="px-6 pt-4 flex gap-2 shrink-0">
                        <button
                            onClick={() => setTab('chefs')}
                            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider ${
                                tab === 'chefs' ? 'bg-brand-sage text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                            }`}
                        >
                            {t('chefs')} ({chefs.length})
                        </button>
                        <button
                            onClick={() => setTab('requests')}
                            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider ${requestsTabClass}`}
                        >
                            {t('chefRequests')} {pendingRequests.length > 0 && `(${pendingRequests.length})`}
                        </button>
                    </div>

                    {tab === 'chefs' && (
                        <div className="px-6 pt-4 shrink-0 space-y-3">
                            <SearchField
                                value={search}
                                onChange={setSearch}
                                placeholder={t('searchChefsPlaceholder')}
                                clearLabel={t('clearSearch')}
                                inputClassName="py-3 rounded-xl focus:ring-brand-sage/30"
                            />
                            <div className="grid grid-cols-4 gap-2">
                                <button
                                    type="button"
                                    onClick={() => downloadChefCsv(chefs)}
                                    className={`${actionBtn} bg-brand-sage text-white shadow-md shadow-brand-sage/20 hover:opacity-90`}
                                >
                                    <ExportIcon className="w-4 h-4 shrink-0" />
                                    <span className="text-center">{t('exportChefCsvShort')}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={importing}
                                    className={`${actionBtn} bg-brand-orange text-white shadow-md shadow-brand-orange/20 hover:opacity-90 disabled:opacity-50`}
                                >
                                    <ImportIcon className="w-4 h-4 shrink-0" />
                                    <span className="text-center">{importing ? '...' : t('importChefCsvShort')}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCreatingChefDraft(buildNewChefDraft(user.language === 'he' ? 'he' : 'en'));
                                        setIsCreatingChef(true);
                                    }}
                                    className={`${actionBtn} bg-brand-sage text-white shadow-md shadow-brand-sage/20 hover:opacity-90`}
                                >
                                    <PlusIcon className="w-4 h-4 shrink-0" />
                                    <span className="text-center">{t('newChefShort')}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsMergeOpen(true)}
                                    className={`${actionBtn} bg-brand-orange text-white shadow-md shadow-brand-orange/20 hover:opacity-90`}
                                >
                                    <MergeIcon className="w-4 h-4 shrink-0" />
                                    <span className="text-center">{t('mergeChefsShort')}</span>
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex-grow overflow-y-auto p-6 space-y-3">
                        {tab === 'requests' && (
                            pendingRequests.length === 0 ? (
                                <p className="text-center text-slate-400 py-8 text-sm">{t('noPendingRequests')}</p>
                            ) : (
                                pendingRequests.map((req) => {
                                    const targetChef = req.targetChefId ? chefs.find((c) => c.id === req.targetChefId) : null;
                                    return (
                                        <div key={req.id} className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 border border-slate-100 dark:border-slate-700">
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                                <div>
                                                    <p className="font-bold text-slate-800 dark:text-slate-100 text-sm">
                                                        {req.requestType === 'existing'
                                                            ? targetChef ? getChefDisplayName(targetChef, user.language || 'en') : req.targetChefId
                                                            : req.proposedEnglishName || req.proposedName}
                                                    </p>
                                                    <p className="text-[10px] text-slate-400">{req.userEmail} · {req.requestType === 'existing' ? t('linkExistingChef') : t('requestNewChef')}</p>
                                                </div>
                                                <span className="text-[9px] font-black uppercase px-2 py-1 bg-amber-100 text-amber-700 rounded-full">{t('pending')}</span>
                                            </div>
                                            {req.message && <p className="text-sm text-slate-600 dark:text-slate-400 italic mb-3">"{req.message}"</p>}
                                            {req.requestType === 'new' && req.proposedName && (
                                                <p className="text-xs text-slate-500 mb-2">{req.proposedName}</p>
                                            )}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleApprove(req)}
                                                    disabled={actionLoading === req.id}
                                                    className="flex-1 py-2 bg-green-600 text-white font-black rounded-xl text-[10px] uppercase tracking-wider disabled:opacity-50"
                                                >
                                                    {t('approveChefRequest')}
                                                </button>
                                                <button
                                                    onClick={() => handleDeny(req.id)}
                                                    disabled={actionLoading === req.id}
                                                    className="flex-1 py-2 bg-red-500 text-white font-black rounded-xl text-[10px] uppercase tracking-wider disabled:opacity-50"
                                                >
                                                    {t('denyChefRequest')}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )
                        )}

                        {tab === 'chefs' && filteredChefs.map((chef) => (
                            <div
                                key={chef.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setViewingChef(chef)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setViewingChef(chef);
                                    }
                                }}
                                className={`bg-white dark:bg-slate-800/30 rounded-2xl p-4 border border-slate-100 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors ${chef.disabled ? 'opacity-60' : ''}`}
                            >
                                <div className="flex justify-between items-center gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <ChefAvatar
                                            imageUrl={chef.imageUrl}
                                            name={getChefDisplayName(chef, user.language || 'en')}
                                            size="xs"
                                            variant="thumb"
                                        />
                                        <div className="min-w-0">
                                            <p className={`font-bold text-slate-800 dark:text-slate-100 text-sm ${chef.disabled ? 'line-through' : ''}`}>
                                                {getChefDisplayName(chef, user.language || 'en')}
                                            </p>
                                            {chef.description?.trim() && (
                                                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                                                    {chef.description.trim()}
                                                </p>
                                            )}
                                            <p className="text-[10px] text-slate-400 truncate">
                                                {chef.nickname}
                                                {chef.originCountry ? ` · ${t('region')}: ${getCountryDisplayName(chef.originCountry, user.language)}` : ''}
                                                {chef.linkedUserId ? ` · ${t('linked')}` : ''}
                                                {chef.disabled ? ` · ${t('disabled')}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingChef(chef);
                                        }}
                                        className="shrink-0 px-4 py-2 text-xs font-black text-brand-sage uppercase tracking-wider border border-brand-sage/30 rounded-xl hover:bg-brand-sage/5 transition-colors"
                                    >
                                        {t('edit')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <ChefProfileModal
                isOpen={!!viewingChef}
                onClose={() => setViewingChef(null)}
                chef={viewingChef}
                recipes={recipes}
                language={user.language}
                onEdit={viewingChef ? () => {
                    setEditingChef(viewingChef);
                    setViewingChef(null);
                } : undefined}
            />

            <ChefEditModal
                isOpen={!!editingChef}
                onClose={() => setEditingChef(null)}
                chef={editingChef}
                language={user.language}
                onSave={onUpdateChef}
            />

            <ChefEditModal
                isOpen={isCreatingChef}
                onClose={() => {
                    setIsCreatingChef(false);
                    setCreatingChefDraft(null);
                }}
                chef={creatingChefDraft}
                isCreate
                language={user.language}
                onSave={onUpdateChef}
                onCreate={onCreateChef}
            />

            <ChefMergeModal
                isOpen={isMergeOpen}
                onClose={() => setIsMergeOpen(false)}
                chefs={chefs}
                recipes={recipes}
                language={user.language}
                onMerge={async (keepId, mergeId) => {
                    const message = await onMergeChefs(keepId, mergeId);
                    setMergeSuccessMessage(message);
                }}
            />

            <InfoNoticeModal
                isOpen={!!mergeSuccessMessage}
                title={t('mergeChefs')}
                message={mergeSuccessMessage}
                variant="success"
                language={user.language}
                onClose={() => setMergeSuccessMessage('')}
            />
        </>
    );
};

export default ChefManagementModal;

function deriveChefIdFromUserNickname(userNickname: string): string {
    return userNickname
        .toLowerCase()
        .replace(/[^a-z0-9\s_-]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_');
}

export function buildChefFromRequest(
    request: ChefRequest,
    userNickname?: string,
): Omit<Chef, 'id'> & { id: string } {
    const name = request.proposedName?.trim() || request.proposedEnglishName?.trim() || '';
    const englishName = request.proposedEnglishName?.trim() || request.proposedName?.trim() || '';
    const origin = inferChefOrigin(name || englishName);
    const nicknameFromUser = userNickname?.trim() ? deriveChefIdFromUserNickname(userNickname) : '';
    const nickname = nicknameFromUser
        || englishName
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .trim()
            .replace(/\s+/g, '_')
        || `chef_${request.userId.slice(0, 8)}`;
    const now = new Date().toISOString();
    return {
        id: nickname,
        name: name || englishName,
        englishName,
        nickname,
        originLanguage: origin.originLanguage,
        ...(origin.originCountry ? { originCountry: origin.originCountry } : {}),
        ...(request.website ? { website: request.website } : {}),
        linkedUserId: request.userId,
        createdAt: now,
        updatedAt: now,
    };
}
